import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Key } from 'lucide-react';

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for tokens in both URL hash and query parameters (Supabase can use either)
    let accessToken = searchParams.get('access_token');
    let refreshToken = searchParams.get('refresh_token');
    
    // If not in query params, check URL hash fragments
    if (!accessToken || !refreshToken) {
      const hash = window.location.hash.substring(1); // Remove the # symbol
      const hashParams = new URLSearchParams(hash);
      accessToken = hashParams.get('access_token');
      refreshToken = hashParams.get('refresh_token');
    }
    
    if (!accessToken || !refreshToken) {
      toast.error('Invalid reset link. Please request a new password reset.');
      navigate('/admin');
      return;
    }

    console.log('Setting session with reset tokens');

    // Set the session with the tokens from the URL
    const setSession = async () => {
      try {
        const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
        
        if (error) {
          console.error('Error setting session:', error);
          toast.error('Invalid reset link. Please request a new password reset.');
          navigate('/admin');
        } else {
          console.log('Session set successfully for password reset');
        }
      } catch (error) {
        console.error('Unexpected error setting session:', error);
        toast.error('Invalid reset link. Please request a new password reset.');
        navigate('/admin');
      }
    };
    
    setSession();
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      
      // Check if this is a temporary password reset (from onboarding flow)
      const urlParams = new URLSearchParams(window.location.search);
      const isTemp = urlParams.get('temp') === 'true';
      
      const updateData: any = {
        password: newPassword
      };
      
      // If this is from onboarding flow, set the temporary_password flag
      // If this is forgot password flow, clear the temporary_password flag
      if (isTemp) {
        updateData.data = {
          temporary_password: true
        };
        console.log('Setting temporary_password flag for onboarding flow');
      } else {
        updateData.data = {
          temporary_password: false
        };
        console.log('Clearing temporary_password flag for forgot password flow');
      }
      
      const { error } = await supabase.auth.updateUser(updateData);

      if (error) {
        console.error('Password update error:', error);
        toast.error('Failed to update password. Please try again.');
      } else {
        const successMessage = isTemp 
          ? 'Password updated successfully! This is a temporary password that will need to be changed on next login.'
          : 'Password updated successfully! Redirecting to login...';
        
        toast.success(successMessage);
        
        // Wait a moment for the toast, then do a full page reload to refresh the session
        setTimeout(() => {
          // Use window.location.href instead of navigate to force a fresh session
          window.location.href = '/admin';
        }, 1500);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 space-y-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-4">
            <Key className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Reset Your Password</h1>
          <p className="mt-2 text-gray-600">Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new password"
                disabled={loading}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Confirm new password"
                disabled={loading}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50"
          >
            {loading ? 'Updating Password...' : 'Update Password'}
          </button>
        </form>

        <div className="text-center">
          <button
            onClick={() => navigate('/admin')}
            className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
} 